/**
 * StatementContinuityGapProvider - iter1235
 *
 * Watches BackgroundJobsContext for jobs that were rejected by the
 * server-side continuity gate (`summary.reason` = 'balance_gap',
 * 'date_gap', or 'first_upload_after_start_date'). Renders a blocking,
 * human-friendly dialog that:
 *   - Tells the user WHY the statement can't be posted
 *   - Shows the specific balance / date gap
 *   - Offers a "Try another file" primary action that dismisses the
 *     job from the tracker, freeing the user to upload again
 *
 * Mounts once in DashboardLayout so any page (Home, Bills, My Banks,
 * etc.) surfaces the guidance right after the upload comes back.
 *
 * Bank-feed guarantees: 100% read-only. Does NOT touch dedup, matching,
 * classification, or auto-posting - it just consumes the same summary
 * the server already produced.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';

const CONTINUITY_REASONS = new Set([
  'balance_gap',
  'date_gap',
  'first_upload_after_start_date',
]);

const REASON_TITLES = {
  balance_gap: 'A statement is missing between your last upload and this one',
  date_gap: 'There is a gap between your last statement and this one',
  first_upload_after_start_date: 'Your first upload starts after your accounting start date',
};

function formatCurrency(value, currency) {
  const num = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency || ''} ${num.toLocaleString()}`;
  }
}

function ContinuityBody({ job }) {
  const details = job.summary?.continuity || {};
  const reason = job.summary?.reason;

  if (reason === 'balance_gap') {
    return (
      <div className="space-y-3 text-sm">
        <p>{job.summary?.message}</p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex justify-between">
            <span className="text-amber-800">Closing balance on file</span>
            <span className="font-semibold text-amber-900" data-testid="continuity-last-close">
              {formatCurrency(details.last_closing_balance, details.currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-amber-800">Opening balance of this statement</span>
            <span className="font-semibold text-amber-900" data-testid="continuity-new-open">
              {formatCurrency(details.new_opening_balance, details.currency)}
            </span>
          </div>
          <div className="flex justify-between border-t border-amber-200 pt-1 mt-1">
            <span className="text-amber-900">Difference</span>
            <span className="font-semibold text-red-700" data-testid="continuity-delta">
              {formatCurrency(
                Math.abs(
                  Number(details.new_opening_balance || 0)
                  - Number(details.last_closing_balance || 0)
                ),
                details.currency,
              )}
            </span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Upload the missing statement(s) between{' '}
          <strong>{details.last_transaction_date || '—'}</strong> and{' '}
          <strong>{details.new_first_date || '—'}</strong> first, then try
          this one again.
        </p>
      </div>
    );
  }

  if (reason === 'date_gap') {
    return (
      <div className="space-y-3 text-sm">
        <p>{job.summary?.message}</p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex justify-between">
            <span className="text-amber-800">Your last transaction on file</span>
            <span className="font-semibold text-amber-900">
              {details.last_transaction_date || '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-amber-800">This statement starts</span>
            <span className="font-semibold text-amber-900">
              {details.new_first_date || '—'}
            </span>
          </div>
          <div className="flex justify-between border-t border-amber-200 pt-1 mt-1">
            <span className="text-amber-900">Missing days</span>
            <span className="font-semibold text-red-700">
              {details.gap_days ?? '—'}
            </span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Upload the statement that covers those days first, or upload a
          combined statement that starts on or before your last on-file
          transaction date.
        </p>
      </div>
    );
  }

  if (reason === 'first_upload_after_start_date') {
    return (
      <div className="space-y-3 text-sm">
        <p>{job.summary?.message}</p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex justify-between">
            <span className="text-amber-800">Your accounting start date</span>
            <span className="font-semibold text-amber-900">
              {details.accounting_start_date || '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-amber-800">This statement starts</span>
            <span className="font-semibold text-amber-900">
              {details.new_first_date || '—'}
            </span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Please upload the statement that covers your accounting start
          date first so we can anchor your opening balance. After that,
          you can upload later statements normally.
        </p>
      </div>
    );
  }

  return <p className="text-sm">{job.summary?.message}</p>;
}

const GlobalStatementContinuityGapProvider = () => {
  const { jobs, removeJob } = useBackgroundJobs();
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [openJob, setOpenJob] = useState(null);

  const flaggedJob = useMemo(() => {
    return (jobs || []).find(
      (j) =>
        j.status === 'failed'
        && CONTINUITY_REASONS.has(j.summary?.reason)
        && !dismissedIds.has(j.jobId),
    );
  }, [jobs, dismissedIds]);

  useEffect(() => {
    if (flaggedJob) setOpenJob(flaggedJob);
  }, [flaggedJob]);

  const handleDismiss = () => {
    if (!openJob) return;
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(openJob.jobId);
      return next;
    });
    try { removeJob?.(openJob.jobId); } catch (_e) { /* noop */ }
    setOpenJob(null);
  };

  if (!openJob) return null;

  const title = REASON_TITLES[openJob.summary?.reason]
    || 'This statement can\u2019t be posted yet';

  return (
    <Dialog open={!!openJob} onOpenChange={(o) => !o && handleDismiss()}>
      <DialogContent className="max-w-lg" data-testid="statement-continuity-gap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {openJob.filename ? (
              <>File: <strong>{openJob.filename}</strong></>
            ) : (
              'Uploaded statement'
            )}
          </DialogDescription>
        </DialogHeader>
        <ContinuityBody job={openJob} />
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            data-testid="continuity-gap-dismiss"
          >
            Close
          </Button>
          <Button
            className="bg-[#108981] hover:bg-[#0d7168]"
            onClick={handleDismiss}
            data-testid="continuity-gap-try-another"
          >
            Got it — I\u2019ll try another file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalStatementContinuityGapProvider;
